/**
 * useThemeMode Hook
 * 
 * Manages light/dark mode state and persistence
 */

import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem } from '../services/storage.service';

export type ThemeMode = 'light' | 'dark';

export interface UseThemeModeReturn {
  mode: ThemeMode;
  isDarkMode: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

export const useThemeMode = (defaultMode: ThemeMode = 'dark'): UseThemeModeReturn => {
  const [mode, setModeState] = useState<ThemeMode>(defaultMode);
  const [mounted, setMounted] = useState(false);

  // Load mode from localStorage on mount
  useEffect(() => {
    const savedMode = getStorageItem<ThemeMode>('mode');
    if (savedMode && ['light', 'dark'].includes(savedMode)) {
      setModeState(savedMode);
    }
    setMounted(true);
  }, []);

  // Persist mode changes and update document class
  useEffect(() => {
    if (mounted) {
      setStorageItem('mode', mode);
      
      // Update document class for Tailwind dark mode
      if (mode === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      // Dispatch event for any listeners
      window.dispatchEvent(new CustomEvent('modeChanged', { detail: { mode } }));
    }
  }, [mounted, mode]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState(prevMode => prevMode === 'light' ? 'dark' : 'light');
  }, []);

  return {
    mode,
    isDarkMode: mode === 'dark',
    setMode,
    toggleMode
  };
};

